import React, { FC } from 'react';
import {
  NavigationLayout,
  NavigationHeader,
  FlexGroup,
  Button,
  useNavigationSize,
  NavigationFooter,
  FlexItem,
  Tooltip,
  Avatar,
  Text,
  Popover,
  Icon,
  Link,
  NavigationGroup,
  NavigationItem,
  NavigationItemProps,
} from '@lithos';
import Wordmark from '../ComponentRedesign/Icons/Wordmark';
import Logo from '../ComponentRedesign/Icons/Logo';
import { useUserSettings } from '../../context/UserSettingsContext';
import { logout } from '../../../utils/okta';
import WorkspaceSelector from './WorkspaceSelector';
import styled from '@emotion/styled';
import { kebabCase } from 'lodash';
import { Link as RouterLink } from 'react-router-dom';

interface SideNavigationProps {
  setShowSearchModal: React.Dispatch<React.SetStateAction<boolean>>;
  generalItems: NavigationItemProps[];
  workspaceItems: NavigationItemProps[];
}

const Header = ({ setShowSearchModal }: { setShowSearchModal: React.Dispatch<React.SetStateAction<boolean>> }) => {
  const { setSize } = useNavigationSize();

  const { workspace } = useUserSettings();
  return (
    <NavigationHeader>
      <div className="full-size" style={{ width: '100%' }}>
        <FlexGroup direction="column" gapSize="4" padding="0">
          <FlexGroup justifyContent="space-between" alignItems="center" padding="0">
            <FlexItem grow="1" shrink="0">
              <RouterLink to={`repo/${workspace}/home/`}>
                <FlexGroup alignItems="center" justifyContent="flex-start" padding="0">
                  <Wordmark />
                </FlexGroup>
              </RouterLink>
            </FlexItem>

            <FlexItem grow="0">
              <Button
                type="ghost"
                icon="Collapse"
                onClick={() => {
                  setSize('forceCompressed');
                }}
              />
            </FlexItem>
          </FlexGroup>
          <Button
            shortcut="K"
            type="secondary"
            label="Search"
            iconLeft="MagnifyingGlass"
            fullWidth
            onClick={() => setShowSearchModal(true)}
          />
        </FlexGroup>
      </div>

      <div className="compressed">
        <FlexGroup direction="column" gapSize="0" padding="0" justifyContent="center">
          <div>
            <RouterLink to={`repo/${workspace}/home/`}>
              <Logo />
            </RouterLink>
          </div>

          <Button
            type="ghost"
            icon="Expand"
            onClick={() => {
              setSize('forceExpanded');
            }}
          />
        </FlexGroup>
      </div>
    </NavigationHeader>
  );
};

const ScrollWrapper = styled.div`
  min-height: 100%;
  overflow: scroll;

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-gutter: stable;
`;

const Content = ({
  generalItems,
  workspaceItems,
}: {
  generalItems: NavigationItemProps[];
  workspaceItems: NavigationItemProps[];
}) => {
  const { workspace, canShowMainApplication, isLoadingWorkspaces } = useUserSettings();

  return (
    <>
      <ScrollWrapper>
        <FlexGroup padding="0" direction="column" gapSize="4">
          <FlexItem grow={1} shrink={0}>
            <NavigationGroup
              beforeItems={canShowMainApplication ? [<WorkspaceSelector />] : []}
              label="Workspace"
              items={workspaceItems.map((item) => {
                const to = item.url?.replace(`:workspace`, workspace ?? 'prod').replace('/*', '');
                const testId = kebabCase(`sidenav-${item.name}`);
                return <NavigationItem name={item.name} icon={item.icon} url={to} testId={testId} />;
              })}
            />
          </FlexItem>
          <FlexItem grow={0} shrink={0}>
            <NavigationGroup
              label="Account"
              items={generalItems.map((item: NavigationItemProps) => {
                const to = item.url?.replace(`:workspace`, workspace ?? 'prod').replace('/*', '');
                const testId = kebabCase(`sidenav-${item.name}`);
                return <NavigationItem name={item.name} icon={item.icon} url={to} testId={testId} />;
              })}
            />
          </FlexItem>
        </FlexGroup>
      </ScrollWrapper>
    </>
  );
};

const SupportPopover = () => {
  return (
    <>
      <FlexGroup direction="column" gapSize="2" alignItems="flex-start">
        <Link url="https://docs.tecton.ai/" icon="Documentation" name="Documentation" newTab />
        <Link url="https://www.tecton.ai/whats-new/" icon="Star" name="What's New" newTab />
      </FlexGroup>
    </>
  );
};

const AccountPopoverContent = () => {
  const { user, oktaAuth } = useUserSettings();
  return (
    <FlexGroup direction="column" gapSize="2">
      <Link name="My Account" icon="UserCircle" url={`/settings/accounts-and-access/users/${user?.email}/workspaces`} />
      <Link name="Log Out" icon="LogOut" onClick={() => logout(oktaAuth)} />
    </FlexGroup>
  );
};

const AccountWrapper = styled.div`
  cursor: pointer;
  border-radius: ${({ theme }) => theme.v1.size['2']}px;
  &:hover {
    background-color: ${({ theme }) => theme.v1.colors.background.hover};
  }
`;

const Footer = () => {
  const { user } = useUserSettings();

  return (
    <NavigationFooter>
      <div className="full-size" style={{ width: '100%' }}>
        <FlexGroup>
          <FlexItem grow={1} overflow="hidden">
            <FlexGroup justifyContent="space-between" alignItems="center">
              <AccountWrapper>
                <Popover
                  placement="top-start"
                  trigger={
                    <FlexGroup justifyContent="space-between" alignItems="center" css={{ cursor: 'pointer' }}>
                      <Avatar name={user?.name ?? ''} />
                      <Text size="s">{user?.name}</Text>
                      <Icon type="mono" icon="ChevronUp" size="s" />
                    </FlexGroup>
                  }
                  body={<AccountPopoverContent />}
                />
              </AccountWrapper>

              <Popover placement="top" trigger={<Button icon="Support" type="ghost" />} body={<SupportPopover />} />
            </FlexGroup>
          </FlexItem>
        </FlexGroup>
      </div>

      <div className="compressed">
        <FlexGroup direction="column" justifyContent="center">
          <Tooltip
            trigger={
              <Popover placement="right" trigger={<Button icon="Support" type="ghost" />} body={<SupportPopover />} />
            }
            content="Support"
            placement="right-start"
          />
          <Tooltip
            trigger={<Popover trigger={<Avatar name={user?.name ?? ''} />} body={<AccountPopoverContent />} />}
            content="Account"
            placement="right-start"
            delay={1}
          />
        </FlexGroup>
      </div>
    </NavigationFooter>
  );
};

const SideNavigation: FC<SideNavigationProps> = ({ setShowSearchModal, generalItems, workspaceItems }) => {
  return (
    <NavigationLayout
      header={<Header setShowSearchModal={setShowSearchModal} />}
      content={<Content generalItems={generalItems} workspaceItems={workspaceItems} />}
      footer={<Footer />}
    />
  );
};

export default SideNavigation;
